import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { createPortal } from "react-dom";
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core";
import { useAnchoredOverlay } from "@/hooks/useAnchoredOverlay";
import type { FormBuilderField } from "../../../types/formBuilder";
import { uploadDisplayImage } from "../../../api/upload";
import { InlineEditable } from "../../../components/inline/InlineEditable";
import {
  builderFieldTypeOptionsForMode,
  isDisplayFieldType,
  type ApiFieldTypeOption,
} from "../../../lib/formBuilder/apiFieldTypes";
import { resolveFieldType } from "../../../lib/forms/normalizeFieldType";
import { defaultPrivacyFieldLabel } from "../../../lib/forms/privacyConsent";
import { getPublicMediaUrl } from "../../../lib/media/getPublicMediaUrl";
import {
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconGripVertical,
  IconPencil,
  IconSettings,
  IconTrash,
} from "../../../components/icons";
import { Checkbox, FieldLabel, TextInput } from "../_components/AdminShell";
import { AdminMenuSelect } from "@/components/admin/AdminMenuSelect";
import { IconButton } from "../_components/IconButton";
import { MinimalHtmlEditor } from "../_components/MinimalHtmlEditor";
import { uniqueFieldName } from "../../../lib/formBuilder/fieldName";
import { CityIdsPicker } from "./CityIdsPicker";
import { ChoicesByMapEditor } from "./ChoicesByMapEditor";
import { ChoicesUrlFields } from "./ChoicesUrlFields";
import {
  VisibilityConditionEditor,
  visibilitySummaryForField,
} from "./VisibilityConditionEditor";
import { useCityNameById } from "./VisibilityCityValue";
import { StringChipsEditor } from "../../../components/admin/StringChipsEditor";
import { parseChoiceLines } from "../../../lib/formBuilder/choiceLines";
import {
  clearListChoicesPatch,
  isListFieldType,
  listChoicesMode,
  patchForListChoicesMode,
} from "../../../lib/formBuilder/listChoicesMode";

export type FieldDragHandleProps = {
  ref: (node: HTMLElement | null) => void;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
};

interface FieldEditorProps {
  field: FormBuilderField;
  fieldNames: string[];
  /** Все поля формы — для ключей источника choices_by. */
  builderFields: FormBuilderField[];
  fieldTypes: ApiFieldTypeOption[];
  onChange: (patch: Partial<FormBuilderField>) => void;
  onRemove: () => void;
  canDuplicate: boolean;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  quizMode?: boolean;
  dragHandleProps?: FieldDragHandleProps;
}

export function FieldEditor({
  field,
  fieldNames,
  builderFields,
  fieldTypes,
  onChange,
  onRemove,
  canDuplicate,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  quizMode = false,
  dragHandleProps,
}: FieldEditorProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [choicesByKey, setChoicesByKey] = useState("");
  const [cityKeyLabels, setCityKeyLabels] = useState<Record<string, string>>(
    {},
  );
  const imageInputRef = useRef<HTMLInputElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsTriggerRef = useRef<HTMLSpanElement>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  useAnchoredOverlay({
    open: settingsOpen,
    triggerRef: settingsTriggerRef,
    overlayRef: settingsMenuRef,
    align: "end",
    matchTriggerWidth: false,
    minHeight: 180,
  });
  const isSelect = field.type === "select";
  const isList = isListFieldType(field.type);
  const choicesMode = listChoicesMode(field);
  const choicesByEnabled = isList && choicesMode === "dependent";
  const showChoices = isList && choicesMode === "manual";
  const showFio = field.type === "fio";
  const isPrivacy = resolveFieldType(field.type) === "privacy";
  const isDisplay = isDisplayFieldType(field.type);
  const otherFields = builderFields.filter(
    (item) => item.name !== field.name && item.name.trim(),
  );
  const cityNames = useCityNameById(field.visibilityEnabled);
  const visibilitySummary = field.visibilityEnabled
    ? visibilitySummaryForField(field, otherFields, cityNames)
    : "";
  const typeOptions = builderFieldTypeOptionsForMode(fieldTypes, quizMode);
  const sourceField = builderFields.find(
    (item) => item.name === field.choicesByField,
  );

  const nextAutoName = (label: string, type: string) => {
    const others = fieldNames.filter((name) => name !== field.name);
    const preferred =
      resolveFieldType(type) === "privacy" ? "privacy" : label.trim() || type;
    return uniqueFieldName(others, preferred);
  };

  const restoreEmptyFieldName = () => {
    if (field.name.trim()) return;
    onChange({
      name: nextAutoName(field.label, field.type),
      nameManuallyEdited: false,
    });
  };

  const stopEditingName = () => {
    restoreEmptyFieldName();
    setIsEditingName(false);
  };

  useEffect(() => {
    if (!settingsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const el = event.target as Element | null;
      if (settingsRef.current?.contains(target)) return;
      if (settingsMenuRef.current?.contains(target)) return;
      if (el?.closest?.("[data-admin-overlay]")) return;
      if (!field.name.trim()) {
        onChange({
          name: nextAutoName(field.label, field.type),
          nameManuallyEdited: false,
        });
      }
      setSettingsOpen(false);
      setIsEditingName(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [field.label, field.name, field.type, fieldNames, onChange, settingsOpen]);

  const handleTypeChange = (type: string) => {
    const namePatch = field.nameManuallyEdited
      ? {}
      : { name: nextAutoName(field.label, type) };
    const leavingList = isListFieldType(type) ? {} : clearListChoicesPatch();

    if (resolveFieldType(type) === "privacy") {
      onChange({
        type,
        label: defaultPrivacyFieldLabel(),
        ...namePatch,
        ...leavingList,
      });
      return;
    }

    if (resolveFieldType(type) === "display") {
      onChange({ type, required: false, ...namePatch, ...leavingList });
      return;
    }

    if (resolveFieldType(type) === "city") {
      onChange({
        type,
        choicesText: "",
        cityIds: [],
        cityLabels: {},
        ...namePatch,
        ...leavingList,
      });
      return;
    }

    if (isPrivacy) {
      onChange({ type, label: "", ...namePatch, ...leavingList });
      return;
    }

    onChange({ type, ...namePatch, ...leavingList });
  };

  const handleImageSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImageUploadError(null);
    setImageUploading(true);
    try {
      const imageUrl = await uploadDisplayImage(file);
      onChange({ displayImageUrl: imageUrl });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Не удалось загрузить картинку";
      setImageUploadError(message);
    } finally {
      setImageUploading(false);
    }
  };

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm sm:p-3">
      <div className="flex items-start gap-2">
        {dragHandleProps ? (
          <button
            type="button"
            ref={dragHandleProps.ref}
            className="mt-1 shrink-0 cursor-grab touch-none rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 active:cursor-grabbing"
            title="Перетащить"
            aria-label="Перетащить поле"
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
          >
            <IconGripVertical size={14} />
          </button>
        ) : null}
        <span
          className="mt-1 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-slate-500"
          title={`Поле ${field.order + 1}`}
        >
          {field.order + 1}
        </span>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {!isPrivacy ? (
                <InlineEditable
                  value={field.label}
                  placeholder={
                    isDisplay ? "Заголовок (необязательно)" : "Название поля"
                  }
                  onChange={(label) => {
                    if (field.nameManuallyEdited) {
                      onChange({ label });
                      return;
                    }
                    onChange({
                      label,
                      name: nextAutoName(label, field.type),
                    });
                  }}
                  className="font-onest text-base font-semibold text-slate-800"
                  emptyClassName="font-onest text-base font-semibold text-slate-400"
                />
              ) : null}
              <div className="mt-0.5 w-fit max-w-full">
                <AdminMenuSelect
                  variant="subtle"
                  ariaLabel="Тип поля"
                  value={field.type}
                  options={typeOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                    group: option.group,
                  }))}
                  onChange={handleTypeChange}
                />
              </div>
              {field.yandexType ? (
                <p className="mt-1 font-onest text-[10px] text-amber-700">
                  Яндекс: {field.yandexType}
                </p>
              ) : null}
              {field.visibilityEnabled ? (
                <p className="mt-1 font-onest text-[11px] leading-snug text-slate-500">
                  {visibilitySummary}
                </p>
              ) : null}
            </div>

            <div className="flex shrink-0 items-start gap-0.5">
              <div ref={settingsRef} className="relative">
                <span ref={settingsTriggerRef} className="inline-flex">
                  <IconButton
                    title={
                      visibilitySummary
                        ? `Настройки поля · ${visibilitySummary}`
                        : "Настройки поля"
                    }
                    size="sm"
                    variant={
                      settingsOpen || field.visibilityEnabled
                        ? "brand"
                        : "default"
                    }
                    className="!h-7 !w-7"
                    onClick={() => {
                      setSettingsOpen((open) => !open);
                      if (settingsOpen) stopEditingName();
                    }}
                  >
                    <IconSettings size={14} />
                  </IconButton>
                </span>
                {field.visibilityEnabled && !settingsOpen ? (
                  <span
                    className="pointer-events-none absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-brand-500"
                    aria-hidden
                  />
                ) : null}

                {settingsOpen
                  ? createPortal(
                      <div
                        ref={settingsMenuRef}
                        data-admin-overlay=""
                        className="fixed z-50 w-[22rem] max-w-[calc(100vw-1rem)] rounded-lg border border-slate-200 bg-white p-3 shadow-lg"
                        style={{ visibility: "hidden" }}
                      >
                        <div className="space-y-3">
                          <div>
                            <FieldLabel size="compact">Ключ поля</FieldLabel>
                            <div className="mt-1 flex items-center gap-1.5">
                              {isEditingName ? (
                                <TextInput
                                  size="compact"
                                  value={field.name}
                                  placeholder="field_key"
                                  className="min-w-0 flex-1 font-mono"
                                  onChange={(name) =>
                                    onChange({ name, nameManuallyEdited: true })
                                  }
                                  onBlur={stopEditingName}
                                  autoFocus
                                />
                              ) : (
                                <code className="min-w-0 flex-1 truncate rounded bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-600">
                                  {field.name || "—"}
                                </code>
                              )}
                              <IconButton
                                title={
                                  isEditingName ? "Готово" : "Изменить ключ"
                                }
                                size="sm"
                                variant={isEditingName ? "brand" : "default"}
                                className="!h-7 !w-7 shrink-0"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                  if (isEditingName) stopEditingName();
                                  else setIsEditingName(true);
                                }}
                              >
                                <IconPencil size={12} />
                              </IconButton>
                            </div>
                          </div>

                          <div className="border-t border-slate-100 pt-3">
                            <VisibilityConditionEditor
                              field={field}
                              otherFields={otherFields}
                              onChange={onChange}
                            />
                          </div>
                        </div>
                      </div>,
                      document.body,
                    )
                  : null}
              </div>

              <IconButton
                title="Копировать поле"
                size="sm"
                className="!h-7 !w-7"
                disabled={!canDuplicate}
                onClick={onDuplicate}
              >
                <IconCopy size={14} />
              </IconButton>
              <IconButton
                title="Выше"
                size="sm"
                className="!h-7 !w-7"
                disabled={!canMoveUp}
                onClick={onMoveUp}
              >
                <IconChevronUp size={14} />
              </IconButton>
              <IconButton
                title="Ниже"
                size="sm"
                className="!h-7 !w-7"
                disabled={!canMoveDown}
                onClick={onMoveDown}
              >
                <IconChevronDown size={14} />
              </IconButton>
              <IconButton
                title="Удалить поле"
                size="sm"
                variant="danger"
                className="!h-7 !w-7"
                onClick={onRemove}
              >
                <IconTrash size={13} />
              </IconButton>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {isSelect ? (
              <Checkbox
                id={`filterable-${field.clientId}`}
                size="compact"
                checked={field.filterable}
                onChange={(filterable) => onChange({ filterable })}
                label="С поиском"
              />
            ) : null}

            {showFio ? (
              <Checkbox
                id={`patronymic-${field.clientId}`}
                size="compact"
                checked={field.patronymicRequired}
                onChange={(patronymicRequired) =>
                  onChange({ patronymicRequired })
                }
                label="Отчество обяз."
              />
            ) : null}

            {field.type === "date" || field.type === "date-range" ? (
              <Checkbox
                id={`with-time-${field.clientId}`}
                size="compact"
                checked={field.withTime}
                onChange={(withTime) => onChange({ withTime })}
                label="С временем"
              />
            ) : null}

            {!isDisplay ? (
              <div className="ml-auto shrink-0">
                <Checkbox
                  id={`required-${field.clientId}`}
                  size="compact"
                  checked={field.required}
                  onChange={(required) => onChange({ required })}
                  label="Обязательное"
                />
              </div>
            ) : null}
          </div>

          {field.type === "birthdate" && !field.yandexType ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <FieldLabel size="compact">Мин. возраст</FieldLabel>
                <TextInput
                  size="compact"
                  type="number"
                  value={field.minAge == null ? "" : String(field.minAge)}
                  placeholder="не задан"
                  onChange={(value) => {
                    const trimmed = value.trim();
                    if (!trimmed) {
                      onChange({ minAge: null });
                      return;
                    }
                    const parsed = Number(trimmed);
                    onChange({
                      minAge: Number.isFinite(parsed)
                        ? Math.trunc(parsed)
                        : null,
                    });
                  }}
                />
              </div>
              <div>
                <FieldLabel size="compact">Макс. возраст</FieldLabel>
                <TextInput
                  size="compact"
                  type="number"
                  value={field.maxAge == null ? "" : String(field.maxAge)}
                  placeholder="не задан"
                  onChange={(value) => {
                    const trimmed = value.trim();
                    if (!trimmed) {
                      onChange({ maxAge: null });
                      return;
                    }
                    const parsed = Number(trimmed);
                    onChange({
                      maxAge: Number.isFinite(parsed)
                        ? Math.trunc(parsed)
                        : null,
                    });
                  }}
                />
              </div>
            </div>
          ) : null}

          {isList ? (
            <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50/80 p-0.5">
              {(
                [
                  ["manual", "Вручную"],
                  ["dependent", "Зависимый"],
                  ["url", "По ссылке"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    const source =
                      otherFields.find(
                        (item) => resolveFieldType(item.type) === "city",
                      )?.name ??
                      otherFields[0]?.name ??
                      "";
                    onChange(patchForListChoicesMode(id, field, source));
                    setChoicesByKey("");
                  }}
                  className={[
                    "flex-1 rounded-md px-2 py-1 font-onest text-[11px] font-medium",
                    choicesMode === id
                      ? "bg-white text-ink shadow-sm"
                      : "text-slate-500 hover:text-slate-800",
                  ].join(" ")}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}

          {showChoices ? (
            <div>
              <FieldLabel size="compact">
                {quizMode ? "Варианты — нажмите верный" : "Варианты"}
              </FieldLabel>
              <StringChipsEditor
                values={parseChoiceLines(field.choicesText)}
                placeholder="Вариант и Enter"
                selectedValues={quizMode ? field.quizCorrect : undefined}
                onToggleSelected={
                  quizMode
                    ? (value) => {
                        const selected = field.quizCorrect.includes(value);
                        const next =
                          resolveFieldType(field.type) === "multiple-choice"
                            ? selected
                              ? field.quizCorrect.filter((item) => item !== value)
                              : [...field.quizCorrect, value]
                            : selected
                              ? []
                              : [value];
                        onChange({ quizCorrect: next });
                      }
                    : undefined
                }
                onChange={(choices) =>
                  onChange({
                    choicesText: choices.join("\n"),
                    quizCorrect: field.quizCorrect.filter((item) =>
                      choices.includes(item),
                    ),
                  })
                }
              />
            </div>
          ) : null}

          {quizMode && isList && !showChoices ? (
            <div>
              <FieldLabel size="compact">Верные ответы (value)</FieldLabel>
              <TextInput
                size="compact"
                value={field.quizCorrect.join("\n")}
                placeholder="По одному значению в строке"
                onChange={(value) =>
                  onChange({
                    quizCorrect: value
                      .split(/\r\n|\n|\r/)
                      .map((item) => item.trim())
                      .filter(Boolean),
                  })
                }
              />
            </div>
          ) : null}

          {quizMode && isList ? (
            <div>
              <FieldLabel size="compact">Баллы за верный ответ</FieldLabel>
              <TextInput
                size="compact"
                type="number"
                value={String(field.quizPoints)}
                onChange={(value) => {
                  const parsed = Number(value);
                  onChange({
                    quizPoints:
                      Number.isFinite(parsed) && parsed > 0 ? parsed : 1,
                  });
                }}
              />
            </div>
          ) : null}

          {field.type === "city" ? (
            <CityIdsPicker
              cityIds={field.cityIds}
              cityLabels={field.cityLabels}
              onChange={({ cityIds, cityLabels }) =>
                onChange({ cityIds, cityLabels })
              }
            />
          ) : null}

          {choicesMode === "url" ? (
            <ChoicesUrlFields
              url={field.choicesUrl}
              path={field.choicesPath}
              item={field.choicesItem}
              value={field.choicesValue}
              onUrlChange={(choicesUrl) => onChange({ choicesUrl })}
              onPathChange={(choicesPath) => onChange({ choicesPath })}
              onItemChange={(choicesItem) => onChange({ choicesItem })}
              onValueChange={(choicesValue) => onChange({ choicesValue })}
            />
          ) : null}

          {choicesByEnabled ? (
            <ChoicesByMapEditor
              field={field}
              otherFields={otherFields}
              sourceField={sourceField}
              choicesByKey={choicesByKey}
              cityKeyLabels={cityKeyLabels}
              onChange={onChange}
              onChoicesByKeyChange={setChoicesByKey}
              onCityKeyLabelsChange={setCityKeyLabels}
            />
          ) : null}

          {isDisplay ? (
            <div className="space-y-3">
              <div>
                <FieldLabel size="compact">Текст</FieldLabel>
                <MinimalHtmlEditor
                  size="compact"
                  value={field.displayContent}
                  rows={4}
                  placeholder="Текст инструкции"
                  onChange={(displayContent) => onChange({ displayContent })}
                />
                <p className="mt-1 font-onest text-[11px] text-slate-500">
                  На форме HTML будет санитизирован
                </p>
              </div>

              <div>
                <FieldLabel size="compact">Картинка</FieldLabel>
                {field.displayImageUrl ? (
                  <div className="mb-2">
                    <img
                      src={getPublicMediaUrl(field.displayImageUrl)}
                      alt=""
                      className="max-h-40 max-w-full rounded border border-slate-200 object-contain"
                    />
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageSelect}
                  />
                  <button
                    type="button"
                    className="rounded border border-slate-200 bg-slate-50 px-2.5 py-1 font-onest text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    disabled={imageUploading}
                    onClick={() => imageInputRef.current?.click()}
                  >
                    {imageUploading ? "Загрузка…" : "Выбрать файл"}
                  </button>
                  {field.displayImageUrl ? (
                    <button
                      type="button"
                      className="rounded px-2 py-1 font-onest text-xs text-red-600 hover:bg-red-50"
                      onClick={() => onChange({ displayImageUrl: "" })}
                    >
                      Удалить
                    </button>
                  ) : null}
                </div>
                {imageUploadError ? (
                  <p
                    className="mt-1 font-onest text-xs text-red-600"
                    role="alert"
                  >
                    {imageUploadError}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}
